// filepath: apps/web/src/operator/OperatorTenantsSection.tsx
//
// FR-UI-OPS-2 AC-4 / AC-7 / AC-8 — Tenants section + deprovision
// sub-flow.
//
// Composes, under the new operator shell, the EXISTING
// operator-token-gated deprovision components from
// `apps/web/src/admin/tenants/`:
//   - <TenantList>            — the cross-tenant list (de-provision
//                               action per row);
//   - <DeprovisionDialog>     — the FR-UI-ADM-15 typed-slug
//                               irreversibility gate;
//   - <DeprovisionProgress>   — polls saga status, exposes
//                               resume / cancel.
//
// The deprovision components are imported BY PATH (no barrel —
// `apps/web/src/admin/tenants/` has none). The mutation hooks
// (`useStartDeprovision`) in `./queries` are reused unchanged.
//
// DATA SOURCE: the list is fed from the SAME `useOperatorSummary`
// payload that drives `<SummaryTable>` (read-only, cross-tenant).
// A thin adapter maps `SummaryTenantRow` → `TenantListItem` (see
// `toTenantListItem` below).
//
// SCOPE (AC-4): only de-provision is surfaced in v1. No
// suspend/quiesce button is rendered — that capability has no
// standalone operator endpoint today (descoped to proposed FR-TEN-9).
//
// SECURITY (AC-7): the shell-wide `<OperatorTokenGate>` already
// satisfies the operator-token probe (`window.__PLATFORM_ADMIN_TOKEN__`),
// so `<TenantAdminGuard>` is NOT re-mounted here. The deprovision
// sub-flow reuses the same operator-token probe via the shared
// `adminFetch` wrapper in `queries.ts`. No per-tenant OIDC/JWT is
// introduced (INV-6).

import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "@/i18n/useTranslation";
import { Banner } from "@/design-system";
import { DeprovisionDialog } from "@/admin/tenants/DeprovisionDialog";
import { DeprovisionProgress } from "@/admin/tenants/DeprovisionProgress";
import { TenantList, type TenantListItem } from "@/admin/tenants/TenantList";
import { useStartDeprovision } from "@/admin/tenants/queries";
import {
  useOperatorSummary,
  type SummaryTenantRow,
} from "./useOperatorSummary";

/**
 * Thin adapter: map a summary row to the `TenantListItem` shape that
 * `<TenantList>` consumes.
 *
 * ISS-232 (BA spec amendment, note 17386): the summary payload now
 * carries the four "always-populated" fields (tenantId, name, status,
 * tier) for every tenant — see `useOperatorSummary.SummaryTenantRow`
 * and the LEFT JOIN contract from `internal/obs/summary_handler.go`
 * (MR !1197). The adapter passes them through verbatim:
 *   - `name`  ← `row.name` (= tenant_slug per BA's spec; v1 has no
 *               separate display name);
 *   - `tier`  ← `row.tier` (= 'small' | 'middle' | 'corporate');
 *   - `status`← `row.status` (= server-derived CASE: 'active' |
 *               'disabled' | 'quiesced');
 *   - `tenant_id` ← `row.tenantId` (the alternate UUID per the
 *               BA's alternate-UUID ruling on ISS-298).
 *
 * INV-2: these are display values only, never a security boundary.
 * The server-side `WHERE tenant_id = ...` in the underlying query is
 * the security boundary; the adapter never loosens that.
 *
 * PRE-FIX BEHAVIOUR (the UAT-35 failure on master 246c3c70d):
 * the adapter discarded the new fields and rendered "—" for
 * `tier`/`status`, and omitted `name`. The BA's spec (no em-dash
 * placeholders) was unmet on the rendered DOM even though the
 * backend was returning them correctly. The fix passes the fields
 * through verbatim.
 */
function toTenantListItem(row: SummaryTenantRow): TenantListItem {
  return {
    id: row.tenantId,
    slug: row.slug,
    name: row.name,
    tier: row.tier,
    status: row.status,
  };
}

/**
 * OperatorTenantsSection — the `/tenants` route. Renders the
 * cross-tenant list (from the shared summary cache) and manages the
 * deprovision dialog. On a successful start, navigates to the
 * deep-linkable status route so the saga can be resumed/cancelled
 * via `<DeprovisionProgress>` (AC-8 — sub-flow is router-addressable).
 */
export function OperatorTenantsSection(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, status, refetch } = useOperatorSummary();
  const [selected, setSelected] = React.useState<TenantListItem | null>(null);

  // The start mutation is keyed to the selected tenant. The hook must
  // be called unconditionally; with no selection the id is "" and the
  // mutation is simply not fired until the user confirms in the
  // dialog (by which point `selected` is set and the hook has
  // re-rendered with the real id).
  const startMut = useStartDeprovision(selected?.id ?? "");

  const tenants = React.useMemo<TenantListItem[]>(
    () => (data?.tenants ?? []).map(toTenantListItem),
    [data],
  );

  const isLoading = status === "loading";
  // ISS-232 BA criterion 2: the page shows an error state on
  // (401, 403 or 5xx). Pre-fix, only `fetch-failure` produced an
  // error here — a 401 fell through to the empty-state path (a
  // silent empty list when the call was actually unauthorized),
  // which is the UAT-reported symptom of this issue. Now both
  // `fetch-failure` (5xx / network) and `401-unauthorized`
  // surface the same error banner; 403 collapses into
  // `fetch-failure` (same banner).
  const error =
    status === "fetch-failure" || status === "401-unauthorized"
      ? t("operator.tenants.loadError", "Failed to load tenants.")
      : undefined;

  const onConfirm = (idempotencyKey: string): void => {
    const tenant = selected;
    if (tenant === null) return;
    startMut.mutate(
      { idempotencyKey },
      {
        onSuccess: () => {
          setSelected(null);
          // AC-8 — the status / resume / cancel sub-flow lives at a
          // deep-linkable route.
          navigate(`/tenants/${tenant.id}/deprovision`);
        },
      },
    );
  };

  const onCancel = (): void => {
    if (!startMut.isPending) {
      setSelected(null);
    }
  };

  return (
    <section
      data-testid="operator-tenants-section"
      className="operator-tenants-section"
      aria-labelledby="operator-tenants-title"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-6)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <h1
        id="operator-tenants-title"
        style={{
          fontSize: "var(--text-2xl)",
          fontWeight: "var(--font-extrabold)",
          color: "var(--text-primary)",
          margin: 0,
        }}
      >
        {t("operator.nav.tenants", "Tenants")}
      </h1>
      {error ? (
        <Banner tone="danger" data-testid="operator-tenants-load-error">
          {error}
        </Banner>
      ) : null}
      <TenantList
        tenants={tenants}
        isLoading={isLoading}
        error={error}
        onDeprovision={(tenant) => setSelected(tenant)}
        onRefresh={() => void refetch()}
      />
      {selected ? (
        <DeprovisionDialog
          tenantSlug={selected.slug}
          isStarting={startMut.isPending}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      ) : null}
    </section>
  );
}

/**
 * OperatorDeprovisionRoute — the `/tenants/:tenantId/deprovision`
 * route. Renders `<DeprovisionProgress>` for the path tenant. The
 * progress panel polls the saga status and exposes Resume / Cancel
 * via the shared operator-token-gated mutation hooks. A missing
 * `tenantId` param (should not happen given the route shape) falls
 * back to the Tenants section.
 */
export function OperatorDeprovisionRoute(): React.ReactElement {
  const { tenantId } = useParams();
  if (!tenantId) {
    return <Navigate to="/tenants" replace />;
  }
  return <DeprovisionProgress tenantId={tenantId} />;
}
